import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

export default function Privacy() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const headingClass = isDark ? 'text-white' : 'text-slate-900';
  const subheadingClass = isDark ? 'text-white' : 'text-slate-800';
  const textClass = isDark ? 'text-gray-300' : 'text-slate-600';

  return (
    <StaticPageLayout
      title="Privacy Policy"
      subtitle="Last updated: January 26, 2026"
    >
      <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>1. Introduction</h2>
          <p className={`${textClass} mb-4`}>
            SweetPilot Inc. ("SweetPilot," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI employee platform and services.
          </p>
          <p className={textClass}>
            By using SweetPilot, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>2. Information We Collect</h2>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>2.1 Information You Provide</h3>
          <ul className={`list-disc pl-6 ${textClass} mb-4 space-y-2`}>
            <li><strong>Account Information:</strong> Name, email address, and password when you create an account. You may also sign in using Google OAuth or passwordless magic link authentication.</li>
            <li><strong>Chat Content:</strong> Messages, voice-to-text transcripts, and content you exchange with our AI employees.</li>
            <li><strong>Brain AI Data:</strong> Documents, files, URLs, and information you upload to your knowledge base to train your AI employees.</li>
            <li><strong>Social Media Data:</strong> Content, posts, and account information you provide when connecting social media platforms (e.g., Twitter/X) through our Social Media Manager.</li>
            <li><strong>Communication Data:</strong> Email and WhatsApp messages managed through our Communication module, including connected account credentials.</li>
            <li><strong>Payment Information:</strong> Credit card details and billing address (processed securely by Stripe). We do not store card details on our servers.</li>
            <li><strong>Presentations & Content:</strong> Slides, images, and content generated using our Presentation Builder and other creative tools.</li>
            <li><strong>Communications:</strong> Emails, support tickets, and feedback you send us.</li>
          </ul>

          <h3 className={`text-xl font-semibold ${subheadingClass} mb-3`}>2.2 Information Collected Automatically</h3>
          <ul className={`list-disc pl-6 ${textClass} mb-4 space-y-2`}>
            <li><strong>Usage Data:</strong> How you interact with our platform, features used, credits consumed, and time spent.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers.</li>
            <li><strong>Analytics Data:</strong> We use Google Analytics to collect anonymized usage statistics to improve our service.</li>
            <li><strong>Session Data:</strong> Authentication tokens stored in your browser session to keep you logged in. Each browser tab maintains its own session.</li>
            <li><strong>Cookies:</strong> Session cookies, analytics cookies, and preference cookies (see our Cookie Policy).</li>
            <li><strong>Log Data:</strong> Server logs including access times, pages viewed, and referring URLs.</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>3. How We Use Your Information</h2>
          <p className={`${textClass} mb-4`}>We use collected information to:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Provide, maintain, and improve our AI employee platform and services</li>
            <li>Process your interactions with AI employees using third-party AI model providers</li>
            <li>Manage your Brain AI knowledge base to personalize AI employee responses</li>
            <li>Schedule and publish social media posts on your behalf through connected integrations</li>
            <li>Send and manage emails and WhatsApp messages through our Communication module</li>
            <li>Generate daily summaries and analytics reports for your workspace</li>
            <li>Process transactions, manage credits, and send billing-related information</li>
            <li>Send technical notices, updates, magic link authentication emails, and support messages</li>
            <li>Respond to your comments, questions, and customer service requests</li>
            <li>Monitor and analyze usage patterns and trends to improve the platform</li>
            <li>Detect, prevent, and address technical issues and fraud</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>4. Data Sharing and Disclosure</h2>
          <p className={`${textClass} mb-4`}>We may share your information with:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li><strong>Service Providers:</strong> Third parties that help us operate our platform, including Cloudflare (hosting and CDN), Google Analytics (usage analytics), and Stripe (payment processing).</li>
            <li><strong>AI Model Providers:</strong> We use third-party AI services (such as OpenAI and Anthropic) to power our AI employees. Your chat data is processed by these providers to generate responses but is not used to train their public models.</li>
            <li><strong>Connected Integrations:</strong> When you connect third-party services (e.g., Twitter/X, Google, WhatsApp, Trello), we share necessary data with those platforms to perform the actions you request.</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information.</li>
          </ul>
          <p className={`${textClass} mt-4`}>
            We do <strong>not</strong> sell your personal information to third parties.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>5. Data Security</h2>
          <p className={`${textClass} mb-4`}>
            We implement industry-standard security measures including:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>SSL/TLS encryption for all data in transit</li>
            <li>Encryption for data at rest</li>
            <li>Secure authentication via password, Google OAuth, or magic link</li>
            <li>Per-tab session isolation to prevent unauthorized cross-tab access</li>
            <li>JWT-based authentication tokens with expiration</li>
            <li>Infrastructure hosted on Cloudflare's global network with built-in DDoS protection</li>
            <li>Access controls and role-based permissions</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>6. Data Retention</h2>
          <p className={`${textClass} mb-4`}>
            We retain your information for as long as your account is active or as needed to provide services. You can request deletion of your data at any time. After account deletion:
          </p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li>Chat history is deleted within 30 days</li>
            <li>Brain AI data is deleted within 7 days</li>
            <li>Account information is deleted within 90 days</li>
            <li>Some data may be retained for legal compliance</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>7. Your Rights</h2>
          <p className={`${textClass} mb-4`}>You have the right to:</p>
          <ul className={`list-disc pl-6 ${textClass} space-y-2`}>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your data</li>
            <li><strong>Portability:</strong> Receive your data in a structured format</li>
            <li><strong>Restriction:</strong> Limit how we process your data</li>
            <li><strong>Objection:</strong> Object to certain processing activities</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>
          <p className={`${textClass} mt-4`}>
            To exercise these rights, contact us at privacy@sweetpilot.com.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>8. International Transfers</h2>
          <p className={textClass}>
            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the European Commission.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>9. Children's Privacy</h2>
          <p className={textClass}>
            SweetPilot is not intended for children under 16. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>10. Changes to This Policy</h2>
          <p className={textClass}>
            We may update this Privacy Policy periodically. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-12">
          <h2 className={`text-2xl font-bold ${headingClass} mb-4`}>11. Contact Us</h2>
          <p className={textClass}>
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <ul className={`list-none mt-4 ${textClass} space-y-2`}>
            <li><strong>Email:</strong> privacy@sweetpilot.com</li>
            <li><strong>Address:</strong> SweetPilot Inc., San Francisco, CA, United States</li>
            <li><strong>Data Protection Officer:</strong> dpo@sweetpilot.com</li>
          </ul>
        </section>
      </div>
    </StaticPageLayout>
  );
}
